import type { Platform } from '@sparklayer-io/spark-dash-ui/types/platform';
import type { BillingPlanKey } from '../context/env-context';
import { getIsProduction } from './environment';
import { type BasePlatform, basePlatform } from './platform';

export const features = Object.freeze({
  CUSTOMER_GROUPS_AMEND: 'CUSTOMER_GROUPS_AMEND',
  TEAM_MEMBER_ROLE: 'TEAM_MEMBER_ROLE',
  MULTI_STOCK_LOCATION_SUPPORT: 'MULTI_STOCK_LOCATION_SUPPORT',
  STOCK_DISPLAY: 'STOCK_DISPLAY',
  QUOTING: 'QUOTING',
  INVOICING: 'INVOICING',
  PAYMENTS_VISIBILITY: 'PAYMENTS_VISIBILITY',
  PAYMENT_RULES: 'PAYMENT_RULES',
  WORKFLOWS: 'WORKFLOWS',
  ANALYTICS_NAV: 'ANALYTICS_NAV',
  ANALYTICS: 'ANALYTICS',
  THEMES: 'THEMES',
  SHIPPING: 'SHIPPING',
  COMPANIES: 'COMPANIES',
  COMPANY_SYNC: 'COMPANY_SYNC',
  API_ACCESS: 'API_ACCESS',
  CUSTOM_MAIL_DOMAIN: 'CUSTOM_MAIL_DOMAIN',
  TEAMS_V2: 'TEAMS_V2',
  DISCOUNTS: 'DISCOUNTS',
  DISCOUNTS_REQUIREMENT_USAGE_LIMITS: 'DISCOUNTS_REQUIREMENT_USAGE_LIMITS',
  DISCOUNTS_EXCLUDED_CUSTOMERS: 'DISCOUNTS_EXCLUDED_CUSTOMERS',
  DISCOUNTS_COMPATIBILITY: 'DISCOUNTS_COMPATIBILITY',
  DISCOUNTS_ORDER_RULES: 'DISCOUNTS_ORDER_RULES',
  DISCOUNTS_FREE_PRODUCT: 'DISCOUNTS_FREE_PRODUCT',
  DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF: 'DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF',
  DISCOUNTS_SHIPPING_REWARD: 'DISCOUNTS_SHIPPING_REWARD',
  DISCOUNTS_ADVANCED: 'DISCOUNTS_ADVANCED',
  ACCOUNTING: 'ACCOUNTING',
  XERO: 'XERO',
  QUICKBOOKS: 'QUICKBOOKS',
  FILE_UPLOAD: 'FILE_UPLOAD',
  RECURRING_ORDERS: 'RECURRING_ORDERS',
});

export type FeatureFlag = keyof typeof features;

/**
 * Every plan feature access is gated on, cheapest first.
 *
 * The order is load-bearing, not cosmetic: a plan's position here *is* its rank, which is what
 * `activePlanMeetsOrExceeds` and `comparePlans` compare. Insert new plans at the right position
 * rather than appending.
 */
const ACTIVE_PLAN_KEYS = [
  'development',
  // Free plan
  'basic_free',
  // Starter Plan
  'v2_basic',
  // Starter Plus Plan
  'v2_starter_plus',
  // Growth Plan
  'v2_growth',
  // Pro Plan
  'v2_pro',
  // Custom Plans
  'enterprise',
  'enterprise_onboarding',
  'custom',
] as const satisfies readonly BillingPlanKey[];

export type ActivePlanKey = (typeof ACTIVE_PLAN_KEYS)[number];

/**
 * Plans that clear every `requiredPlan` gate whatever their rank. Dev stores exist to exercise the
 * whole product, so the free `development` plan must not be judged on price like a paid tier.
 *
 * Plan gates only — production readiness stays keyed on the host, in `featureEnabled`.
 */
const PLANS_EXEMPT_FROM_GATES = new Set<ActivePlanKey>(['development']);

function planRank(plan: ActivePlanKey): number {
  return ACTIVE_PLAN_KEYS.indexOf(plan);
}

export function activePlanMeetsOrExceeds(
  activePlan: ActivePlanKey,
  minPlan: ActivePlanKey,
): boolean {
  if (PLANS_EXEMPT_FROM_GATES.has(activePlan)) {
    return true;
  }
  return planRank(activePlan) >= planRank(minPlan);
}

/** Comparator ordering plan keys cheapest first, by the same order that gates feature access. */
export function comparePlans(a: ActivePlanKey, b: ActivePlanKey): number {
  return planRank(a) - planRank(b);
}

function isActiveBillingPlanKey(billingPlanKey?: BillingPlanKey): billingPlanKey is ActivePlanKey {
  if (!billingPlanKey) {
    return false;
  }
  return (ACTIVE_PLAN_KEYS as readonly BillingPlanKey[]).includes(billingPlanKey);
}

export function getActiveBillingPlanKey(
  billingPlanKey?: BillingPlanKey,
): ActivePlanKey | undefined {
  return isActiveBillingPlanKey(billingPlanKey) ? billingPlanKey : undefined;
}

type Feature = {
  enabledForProduction?: boolean;
  /** In production, only Spark staff see the feature. Everyone sees it in development. */
  staffOnlyInProduction?: boolean;
  siteIdOverrides?: string[];
  /** The platforms the feature is allowed on, left out when every platform gets it. */
  enabledPlatforms?: BasePlatform[];
  requiredPlan?: ActivePlanKey;
};

const featureState = Object.freeze({
  [features.CUSTOMER_GROUPS_AMEND]: {
    // Off for BigCommerce, where customer groups sync from their own platform instead.
    enabledPlatforms: ['shopify', 'wix', 'woocommerce', 'magento', 'custom'],
  },
  // Hide team member invite role - currently does not do anything
  [features.TEAM_MEMBER_ROLE]: {
    enabledForProduction: false,
  },
  [features.MULTI_STOCK_LOCATION_SUPPORT]: {
    enabledForProduction: true,
    requiredPlan: 'v2_growth',
  },
  [features.QUOTING]: {
    requiredPlan: 'v2_pro',
  },
  [features.INVOICING]: {
    requiredPlan: 'v2_growth',
  },
  [features.PAYMENTS_VISIBILITY]: {
    enabledForProduction: true,
    enabledPlatforms: ['shopify', 'custom'],
  },
  // An empty override list keeps rules off for every production site while leaving them on in dev.
  // Add a site id to open them up to that store.
  [features.PAYMENT_RULES]: {
    siteIdOverrides: [] as string[],
    requiredPlan: 'v2_growth',
  },
  [features.WORKFLOWS]: {
    requiredPlan: 'v2_growth',
  },
  [features.ANALYTICS_NAV]: {
    enabledForProduction: false,
  },
  [features.ANALYTICS]: {
    requiredPlan: 'v2_pro',
  },
  [features.THEMES]: {
    requiredPlan: 'v2_pro',
  },
  [features.SHIPPING]: {
    requiredPlan: 'v2_basic',
  },
  [features.COMPANIES]: {
    enabledForProduction: false,
  },
  [features.COMPANY_SYNC]: {
    enabledForProduction: false,
  },
  [features.API_ACCESS]: {
    requiredPlan: 'v2_pro',
  },
  [features.CUSTOM_MAIL_DOMAIN]: {
    requiredPlan: 'v2_pro',
  },
  [features.TEAMS_V2]: {
    enabledForProduction: false,
  },
  [features.DISCOUNTS]: {
    requiredPlan: 'v2_basic',
  },
  [features.DISCOUNTS_REQUIREMENT_USAGE_LIMITS]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_EXCLUDED_CUSTOMERS]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_COMPATIBILITY]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_ORDER_RULES]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_FREE_PRODUCT]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_SHIPPING_REWARD]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_ADVANCED]: {
    requiredPlan: 'v2_pro',
  },
  [features.ACCOUNTING]: {
    enabledForProduction: true,
    enabledPlatforms: ['shopify', 'wix', 'custom'],
  },
  [features.XERO]: {
    enabledForProduction: true,
    requiredPlan: 'v2_pro',
    enabledPlatforms: ['shopify', 'custom'],
  },
  [features.QUICKBOOKS]: {
    enabledForProduction: true,
    requiredPlan: 'v2_pro',
    enabledPlatforms: ['shopify', 'wix', 'custom'],
  },
  [features.FILE_UPLOAD]: {
    requiredPlan: 'v2_growth',
  },
  [features.RECURRING_ORDERS]: {
    enabledForProduction: false,
    requiredPlan: 'v2_growth',
  },
}) as Readonly<Record<FeatureFlag, Feature>>;

/** Prefer the `useFeatureEnabled` / `useIsFeatureEnabled` hooks, which fill in site, platform, and staff. */
export function featureEnabled(
  featureFlag: FeatureFlag,
  siteId: string,
  platform: Platform | undefined,
  isSparkStaff: boolean,
): boolean {
  const feature = featureState[featureFlag];

  if (feature?.siteIdOverrides?.includes(siteId)) {
    return true;
  }
  if (getIsProduction() && feature?.staffOnlyInProduction && !isSparkStaff) {
    return false;
  }

  if (getIsProduction() && !feature?.enabledForProduction && feature?.siteIdOverrides) {
    return false;
  }

  const gatedPlatform = basePlatform(platform);
  if (
    gatedPlatform &&
    feature?.enabledPlatforms &&
    !feature.enabledPlatforms.includes(gatedPlatform)
  ) {
    return false;
  }

  if (getIsProduction() && feature?.enabledForProduction === false) {
    return false;
  }

  return true;
}

/**
 * The cheapest plan that unlocks a feature, or `undefined` when no plan gates it.
 *
 * Callers that need to *name* the plan in an upsell read it from here rather than keeping their
 * own copy, so a change to `featureState` moves the message with it.
 */
export function getRequiredPlan(featureFlag: FeatureFlag): ActivePlanKey | undefined {
  return featureState[featureFlag]?.requiredPlan;
}

export function featureRestricted(
  featureFlag: FeatureFlag,
  siteBillingPlan?: ActivePlanKey,
): boolean {
  const feature = featureState[featureFlag];

  if (!feature?.requiredPlan) {
    return false;
  }
  // if the feature requires a plan, check if we even have a billing plan
  if (!siteBillingPlan) {
    return true;
  }

  return !activePlanMeetsOrExceeds(siteBillingPlan, feature.requiredPlan);
}
